import React, { useState, useRef, useEffect } from "react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Bold, Italic, Heading1, Heading2, List, ListOrdered, Link as LinkIcon, Code as CodeIcon, Quote, ExternalLink } from "lucide-react";
import { useConfirm } from "../ConfirmDialog";
import api from "../../lib/api";

// ── Tiny inline markdown renderer ────────────────────────────────────
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]));
}
function renderMarkdown(src) {
  if (!src) return "";
  let html = escapeHtml(src);
  // code blocks ```
  html = html.replace(/```([\s\S]*?)```/g, (_, code) => `<pre class="md-pre">${code}</pre>`);
  // inline code
  html = html.replace(/`([^`\n]+)`/g, (_, code) => `<code class="md-code">${code}</code>`);
  // headings
  html = html.replace(/^### (.+)$/gm, '<h3 class="md-h3">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="md-h2">$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1 class="md-h1">$1</h1>');
  // bold + italic
  html = html.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*([^*\n]+)\*/g, "<em>$1</em>");
  // links [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="md-link">$1</a>');
  // blockquote
  html = html.replace(/^&gt; (.+)$/gm, '<blockquote class="md-quote">$1</blockquote>');
  // lists
  html = html.replace(/(?:^|\n)((?:- .+\n?)+)/g, (m) => {
    const items = m.trim().split("\n").map((l) => l.replace(/^- /, "")).map((t) => `<li>${t}</li>`).join("");
    return `\n<ul class="md-ul">${items}</ul>`;
  });
  html = html.replace(/(?:^|\n)((?:\d+\. .+\n?)+)/g, (m) => {
    const items = m.trim().split("\n").map((l) => l.replace(/^\d+\. /, "")).map((t) => `<li>${t}</li>`).join("");
    return `\n<ol class="md-ol">${items}</ol>`;
  });
  // paragraphs (lines that are still raw text)
  html = html.split(/\n\n+/).map((blk) => {
    if (/^<(h\d|ul|ol|pre|blockquote)/.test(blk.trim())) return blk;
    return `<p class="md-p">${blk.replace(/\n/g, "<br/>")}</p>`;
  }).join("\n");
  return html;
}

const mdStyles = `
.md-h1 { font-family: 'Outfit', sans-serif; font-size: 22px; font-weight: 600; letter-spacing: -0.025em; margin: 0 0 8px; color: #FAFAFA; }
.md-h2 { font-family: 'Outfit', sans-serif; font-size: 17px; font-weight: 600; letter-spacing: -0.02em; margin: 12px 0 6px; color: #FAFAFA; }
.md-h3 { font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 600; letter-spacing: -0.01em; margin: 10px 0 4px; color: #F4F4F5; }
.md-p { font-size: 13px; line-height: 1.7; color: #D4D4D8; margin: 0 0 8px; }
.md-ul, .md-ol { font-size: 13px; line-height: 1.7; color: #D4D4D8; margin: 0 0 8px; padding-left: 20px; }
.md-ul li, .md-ol li { margin: 2px 0; }
.md-link { color: #FAFAFA; text-decoration: underline; text-underline-offset: 3px; }
.md-link:hover { color: #fff; }
.md-code { background: #1A1A1A; border: 1px solid #2A2A2A; padding: 1px 5px; border-radius: 3px; font-family: 'JetBrains Mono', monospace; font-size: 11px; }
.md-pre { background: #0A0A0A; border: 1px solid #2A2A2A; padding: 10px 12px; border-radius: 6px; font-family: 'JetBrains Mono', monospace; font-size: 11px; overflow-x: auto; color: #D4D4D8; margin: 6px 0; }
.md-quote { border-left: 2px solid #3F3F46; padding-left: 10px; color: #A1A1AA; font-style: italic; margin: 6px 0; }
`;

// ── Text (rich) ──────────────────────────────────────────────────────
export function TextBlock({ block, onUpdateBlock, isTeam }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(block.content?.text || "");
  const ref = useRef(null);
  const { promptValue } = useConfirm();

  const wrap = (before, after = "") => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const sel = ta.value.slice(start, end);
    const next = ta.value.slice(0, start) + before + sel + after + ta.value.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + sel.length);
    });
  };
  const insertLine = (prefix) => {
    const ta = ref.current; if (!ta) return;
    const start = ta.selectionStart;
    const before = ta.value.slice(0, start);
    const lineStart = before.lastIndexOf("\n") + 1;
    const next = ta.value.slice(0, lineStart) + prefix + ta.value.slice(lineStart);
    setText(next);
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(start + prefix.length, start + prefix.length); });
  };
  const insertLink = async () => {
    const url = await promptValue({ title: "Insert link", placeholder: "https://…", confirmText: "Insert" });
    if (!url) return;
    wrap("[", `](${url})`);
  };

  const save = async () => { await onUpdateBlock(block.id, { content: { text } }); setEditing(false); };

  if (editing && isTeam) {
    return (
      <div data-testid={`text-block-${block.id}`} className="space-y-2 anim-fade-up">
        <div className="flex items-center gap-0.5 bg-[#0a0a0a] border border-zinc-800 rounded-md px-1 py-1 w-fit">
          <button title="Heading 1" onClick={() => insertLine("# ")} className="h-7 w-7 rounded hover:bg-zinc-800 flex items-center justify-center text-zinc-300"><Heading1 className="w-3.5 h-3.5" /></button>
          <button title="Heading 2" onClick={() => insertLine("## ")} className="h-7 w-7 rounded hover:bg-zinc-800 flex items-center justify-center text-zinc-300"><Heading2 className="w-3.5 h-3.5" /></button>
          <span className="w-px h-4 bg-zinc-800 mx-1" />
          <button title="Bold" onClick={() => wrap("**", "**")} className="h-7 w-7 rounded hover:bg-zinc-800 flex items-center justify-center text-zinc-300"><Bold className="w-3.5 h-3.5" /></button>
          <button title="Italic" onClick={() => wrap("*", "*")} className="h-7 w-7 rounded hover:bg-zinc-800 flex items-center justify-center text-zinc-300"><Italic className="w-3.5 h-3.5" /></button>
          <span className="w-px h-4 bg-zinc-800 mx-1" />
          <button title="Bullet list" onClick={() => insertLine("- ")} className="h-7 w-7 rounded hover:bg-zinc-800 flex items-center justify-center text-zinc-300"><List className="w-3.5 h-3.5" /></button>
          <button title="Numbered list" onClick={() => insertLine("1. ")} className="h-7 w-7 rounded hover:bg-zinc-800 flex items-center justify-center text-zinc-300"><ListOrdered className="w-3.5 h-3.5" /></button>
          <button title="Quote" onClick={() => insertLine("> ")} className="h-7 w-7 rounded hover:bg-zinc-800 flex items-center justify-center text-zinc-300"><Quote className="w-3.5 h-3.5" /></button>
          <button title="Inline code" onClick={() => wrap("`", "`")} className="h-7 w-7 rounded hover:bg-zinc-800 flex items-center justify-center text-zinc-300"><CodeIcon className="w-3.5 h-3.5" /></button>
          <button title="Link" onClick={insertLink} className="h-7 w-7 rounded hover:bg-zinc-800 flex items-center justify-center text-zinc-300"><LinkIcon className="w-3.5 h-3.5" /></button>
        </div>
        <Textarea ref={ref} value={text} onChange={(e) => setText(e.target.value)} rows={8}
          className="bg-[#0a0a0a] border-zinc-800 text-[13px] leading-relaxed font-mono"
          placeholder="Start writing… Use # for headings, **bold**, *italic*, - bullets, [text](url) for links" />
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={() => { setText(block.content?.text || ""); setEditing(false); }} className="text-zinc-500 text-[12px]">Cancel</Button>
          <Button size="sm" onClick={save} className="bg-white hover:bg-zinc-200 text-black text-[12px]">Save</Button>
        </div>
      </div>
    );
  }
  const html = renderMarkdown(block.content?.text);
  return (
    <>
      <style>{mdStyles}</style>
      <div data-testid={`text-block-${block.id}`} onDoubleClick={() => isTeam && setEditing(true)}
        className={isTeam ? "cursor-text" : ""}
        dangerouslySetInnerHTML={{
          __html: html || (isTeam
            ? '<p class="md-p" style="color:#52525B;font-style:italic">Double-click to edit text…</p>'
            : '<p class="md-p" style="color:#52525B">No content.</p>'),
        }} />
    </>
  );
}

// ── Link ──────────────────────────────────────────────────────────────
export function LinkBlock({ block, onUpdateBlock, isTeam, onNavigate }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(block.content?.label || "");
  const [url, setUrl] = useState(block.content?.url || "");
  const save = async () => { await onUpdateBlock(block.id, { content: { label, url } }); setEditing(false); };

  // Derive favicon + hostname from URL
  const host = React.useMemo(() => {
    try {
      const u = block.content?.url || "";
      if (!u) return null;
      return new URL(u.startsWith("http") ? u : `https://${u}`).hostname;
    } catch { return null; }
  }, [block.content?.url]);
  const faviconUrl = host ? `https://www.google.com/s2/favicons?domain=${host}&sz=64` : null;
  const [faviconErr, setFaviconErr] = useState(false);
  React.useEffect(() => { setFaviconErr(false); }, [faviconUrl]);

  if (editing && isTeam) {
    return (
      <div className="space-y-2 tt-card p-3">
        <Input placeholder="Display name (e.g. 'Q4 GMV sheet')" value={label} onChange={(e) => setLabel(e.target.value)} className="bg-transparent border-zinc-800 text-[13px]" />
        <Input placeholder="https://docs.google.com/spreadsheets/..." value={url} onChange={(e) => setUrl(e.target.value)} className="bg-transparent border-zinc-800 text-[13px]" />
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="text-zinc-500 text-[12px]">Cancel</Button>
          <Button size="sm" onClick={save} className="bg-white hover:bg-zinc-200 text-black text-[12px]">Save</Button>
        </div>
      </div>
    );
  }

  const click = () => {
    if (block.content?.url) {
      const u = block.content.url;
      window.open(u.startsWith("http") ? u : `https://${u}`, "_blank", "noopener,noreferrer");
    } else if (block.content?.page_id && onNavigate) {
      onNavigate(block.content.page_id);
    }
  };

  return (
    <button onClick={click} onDoubleClick={() => isTeam && setEditing(true)}
      data-testid={`link-block-${block.id}`}
      className="block w-full text-left tt-card p-3 hover:translate-y-[-2px] hover:border-zinc-700 transition-all group">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
          {faviconUrl && !faviconErr ? (
            <img src={faviconUrl} alt="" onError={() => setFaviconErr(true)} className="w-7 h-7" loading="lazy" />
          ) : (
            <ExternalLink className="w-4 h-4 text-zinc-500" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[13px] font-semibold text-zinc-100 truncate group-hover:text-white">
            {block.content?.label || "Untitled link"}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5 truncate font-mono flex items-center gap-1.5">
            {host || "internal page"}
            {block.content?.url && <ExternalLink className="w-3 h-3 opacity-60" />}
          </div>
        </div>
      </div>
    </button>
  );
}

// ── Embed (URL or HTML) ───────────────────────────────────────────────
export function EmbedBlock({ block, onUpdateBlock, isTeam }) {
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState(block.content?.mode || (block.content?.html ? "html" : "url"));
  const [url, setUrl] = useState(block.content?.url || "");
  const [html, setHtml] = useState(block.content?.html || "");
  const [height, setHeight] = useState(block.content?.height || 480);
  const save = async () => {
    await onUpdateBlock(block.id, { content: { mode, url, html, height } });
    setEditing(false);
  };

  if (editing && isTeam) {
    return (
      <div className="space-y-2 tt-card p-3">
        <div className="flex gap-1.5">
          <button onClick={() => setMode("url")} className={`px-2.5 h-7 rounded text-[11px] uppercase tracking-wider font-medium ${mode === "url" ? "bg-white text-black" : "border border-zinc-800 text-zinc-400"}`}>URL</button>
          <button onClick={() => setMode("html")} className={`px-2.5 h-7 rounded text-[11px] uppercase tracking-wider font-medium ${mode === "html" ? "bg-white text-black" : "border border-zinc-800 text-zinc-400"}`}>HTML</button>
        </div>
        {mode === "url" ? (
          <Input placeholder="https://..." value={url} onChange={(e) => setUrl(e.target.value)} className="bg-transparent border-zinc-800 text-[13px]" />
        ) : (
          <Textarea placeholder='<iframe src="…" />' rows={6} value={html} onChange={(e) => setHtml(e.target.value)} className="bg-[#0a0a0a] border-zinc-800 text-[12px] font-mono" />
        )}
        <Input type="number" placeholder="Height (px)" value={height} onChange={(e) => setHeight(parseInt(e.target.value || "480"))} className="bg-transparent border-zinc-800 text-[13px]" />
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="text-zinc-500 text-[12px]">Cancel</Button>
          <Button size="sm" onClick={save} className="bg-white hover:bg-zinc-200 text-black text-[12px]">Save</Button>
        </div>
      </div>
    );
  }

  const m = block.content?.mode || (block.content?.html ? "html" : "url");
  return (
    <div data-testid={`embed-block-${block.id}`} onDoubleClick={() => isTeam && setEditing(true)}
      className="border border-zinc-800 rounded-lg overflow-hidden bg-[#0a0a0a]">
      {m === "url" && block.content?.url && (
        <iframe src={block.content.url} title="embed" className="w-full" style={{ height: `${block.content?.height || 480}px` }} />
      )}
      {m === "html" && block.content?.html && (
        <div className="overflow-auto" style={{ height: `${block.content?.height || 480}px` }}
          dangerouslySetInnerHTML={{ __html: block.content.html }} />
      )}
      {!(block.content?.url || block.content?.html) && (
        <div className="p-8 text-center text-zinc-600 text-[12px]">{isTeam ? "Double-click to set embed URL or paste HTML" : "No embed configured."}</div>
      )}
    </div>
  );
}

// ── Checklist ────────────────────────────────────────────────────────
export function ChecklistBlock({ block, onUpdateBlock, isTeam }) {
  const items = block.content?.items || [];
  const clientWritable = block.config?.client_writable;
  const [draft, setDraft] = useState("");

  const update = async (newItems) => onUpdateBlock(block.id, { content: { items: newItems } });
  const toggle = (idx) => {
    if (!isTeam && !clientWritable) return;
    update(items.map((it, i) => i === idx ? { ...it, done: !it.done } : it));
  };
  const add = () => {
    if (!draft.trim()) return;
    update([...items, { id: crypto.randomUUID(), text: draft, done: false }]);
    setDraft("");
  };
  const remove = (idx) => update(items.filter((_, i) => i !== idx));

  return (
    <div data-testid={`checklist-block-${block.id}`} className="tt-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-[14px] font-semibold">{block.config?.title || "Checklist"}</h3>
        {isTeam && (
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-500">
            <input type="checkbox" checked={!!clientWritable} onChange={(e) => onUpdateBlock(block.id, { config: { ...block.config, client_writable: e.target.checked } })} />
            Client can tick
          </label>
        )}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={it.id} className="flex items-start gap-3 group" data-testid={`checklist-item-${it.id}`}>
            <input type="checkbox" checked={!!it.done} onChange={() => toggle(i)}
              disabled={!isTeam && !clientWritable} className="mt-1 accent-white" />
            <span className={it.done ? "text-zinc-600 line-through flex-1 text-[13px]" : "text-zinc-200 flex-1 text-[13px]"}>{it.text}</span>
            {isTeam && <button onClick={() => remove(i)} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-[#EF4444] text-[14px] px-1">×</button>}
          </li>
        ))}
        {items.length === 0 && <li className="text-zinc-600 text-[12px]">No items.</li>}
      </ul>
      {isTeam && (
        <div className="flex gap-2 mt-3">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add item…"
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" />
          <Button size="sm" onClick={add} className="bg-white hover:bg-zinc-200 text-black text-[12px] h-8">Add</Button>
        </div>
      )}
    </div>
  );
}

// ── KV Status ────────────────────────────────────────────────────────
export function KVStatusBlock({ block, onUpdateBlock, isTeam }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(block.content?.label || "");
  const [value, setValue] = useState(block.content?.value || "");
  const save = async () => { await onUpdateBlock(block.id, { content: { label, value } }); setEditing(false); };

  if (editing && isTeam) {
    return (
      <div className="tt-card p-4 space-y-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" className="bg-[#0a0a0a] border-zinc-800 text-[13px]" />
        <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Value" className="bg-[#0a0a0a] border-zinc-800 text-[13px]" />
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="text-zinc-500 text-[12px]">Cancel</Button>
          <Button size="sm" onClick={save} className="bg-white hover:bg-zinc-200 text-black text-[12px]">Save</Button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid={`kv-block-${block.id}`} onDoubleClick={() => isTeam && setEditing(true)}
      className="tt-card p-4 hover:translate-y-[-2px]">
      <div className="t-eyebrow">{block.content?.label || (isTeam ? "(set label)" : "")}</div>
      <div className="font-display text-[20px] font-semibold text-zinc-100 mt-1 tracking-tight">{block.content?.value || (isTeam ? "(set value)" : "—")}</div>
    </div>
  );
}

// ── Media (image/video) ───────────────────────────────────────────────
export function MediaBlock({ block, onUpdateBlock, isTeam }) {
  const url = block.content?.url || "";
  const kind = block.content?.kind || "image"; // image | video
  const caption = block.content?.caption || "";
  const [editing, setEditing] = useState(!url);
  const [draftUrl, setDraftUrl] = useState(url);
  const [draftKind, setDraftKind] = useState(kind);
  const [draftCaption, setDraftCaption] = useState(caption);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDraftUrl(url); setDraftKind(kind); setDraftCaption(caption); }, [url, kind, caption]);

  const save = async () => {
    await onUpdateBlock(block.id, { content: { url: draftUrl, kind: draftKind, caption: draftCaption } });
    setEditing(false);
  };

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("tt_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/upload`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      const f = await res.json();
      const newUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}`;
      const ct = (f.content_type || "").toLowerCase();
      const newKind = ct.startsWith("video/") ? "video" : "image";
      setDraftUrl(newUrl); setDraftKind(newKind);
      // Auto-save
      await onUpdateBlock(block.id, { content: { url: newUrl, kind: newKind, caption: draftCaption } });
      setEditing(false);
    } finally { setBusy(false); }
  };

  if (editing && isTeam) {
    return (
      <div className="tt-card p-4 space-y-2">
        <div className="flex gap-1.5">
          <button onClick={() => setDraftKind("image")} className={`px-2.5 h-7 rounded text-[11px] uppercase tracking-wider font-medium ${draftKind === "image" ? "bg-white text-black" : "border border-zinc-800 text-zinc-400"}`}>Image</button>
          <button onClick={() => setDraftKind("video")} className={`px-2.5 h-7 rounded text-[11px] uppercase tracking-wider font-medium ${draftKind === "video" ? "bg-white text-black" : "border border-zinc-800 text-zinc-400"}`}>Video</button>
        </div>
        <Input value={draftUrl} onChange={(e) => setDraftUrl(e.target.value)} placeholder="https://… (or upload)" className="bg-[#0a0a0a] border-zinc-800 text-[13px]" />
        <Input value={draftCaption} onChange={(e) => setDraftCaption(e.target.value)} placeholder="Caption (optional)" className="bg-[#0a0a0a] border-zinc-800 text-[13px]" />
        <label className="block cursor-pointer rounded-md border border-dashed border-zinc-800 hover:border-zinc-600 py-2.5 text-center text-[12px] text-zinc-500 hover:text-zinc-300 transition-all">
          <input type="file" accept="image/*,video/*" className="hidden" disabled={busy}
            onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ""; }} />
          {busy ? "Uploading…" : "Click or drop file to upload"}
        </label>
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="text-zinc-500 text-[12px]">Cancel</Button>
          <Button size="sm" onClick={save} disabled={!draftUrl} className="bg-white hover:bg-zinc-200 text-black text-[12px]">Save</Button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid={`media-block-${block.id}`} onDoubleClick={() => isTeam && setEditing(true)}
      className="tt-card overflow-hidden">
      {url ? (
        <>
          {kind === "video" ? (
            <video src={url} controls className="w-full bg-black" />
          ) : (
            <img src={url} alt={caption || ""} className="w-full block" />
          )}
          {caption && <div className="text-[11px] text-zinc-500 px-3.5 py-2 border-t border-zinc-900">{caption}</div>}
        </>
      ) : (
        <div className="p-8 text-center text-zinc-600 text-[12px]">{isTeam ? "Double-click to add media" : "No media"}</div>
      )}
    </div>
  );
}

// ── File (multi-upload + drag-drop) ──────────────────────────────────
export function FileBlock({ block, onUpdateBlock, isTeam }) {
  const files = block.content?.files || [];
  const clientUploadable = block.config?.client_uploadable;
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem("tt_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/upload`, {
      method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
    });
    return res.json();
  };

  const onUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of Array.from(fileList)) {
        uploaded.push(await uploadOne(f));
      }
      onUpdateBlock(block.id, { content: { files: [...files, ...uploaded] } });
    } finally { setBusy(false); }
  };

  const onInput = (e) => { onUpload(e.target.files); e.target.value = ""; };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    onUpload(e.dataTransfer.files);
  };

  const remove = (id) => {
    if (!isTeam) return;
    onUpdateBlock(block.id, { content: { files: files.filter((f) => f.id !== id) } });
  };

  const canUpload = isTeam || clientUploadable;
  const fmtSize = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;

  return (
    <div data-testid={`file-block-${block.id}`} className="tt-card p-4"
      onDragOver={(e) => { if (canUpload) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={canUpload ? onDrop : undefined}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-[14px] font-semibold">{block.config?.title || "Files"}</h3>
        {isTeam && (
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-500">
            <input type="checkbox" checked={!!clientUploadable} onChange={(e) => onUpdateBlock(block.id, { config: { ...block.config, client_uploadable: e.target.checked } })} />
            Client can upload
          </label>
        )}
      </div>
      <ul className="space-y-1.5">
        {files.map((f) => (
          <li key={f.id} className="flex items-center justify-between gap-3 bg-[#0a0a0a] border border-zinc-800 rounded-md p-2.5 hover:border-zinc-700 transition-colors">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}`} target="_blank" rel="noopener noreferrer"
              className="text-[12px] text-zinc-200 hover:text-white truncate flex-1" data-testid={`file-link-${f.id}`}>{f.filename}</a>
            <span className="text-[10px] text-zinc-600 font-mono">{fmtSize(f.size)}</span>
            {isTeam && <button onClick={() => remove(f.id)} className="text-zinc-600 hover:text-[#EF4444] text-[14px] px-1">×</button>}
          </li>
        ))}
        {files.length === 0 && <li className="text-zinc-600 text-[12px]">No files.</li>}
      </ul>
      {canUpload && (
        <label className={`mt-3 block cursor-pointer rounded-md border border-dashed py-3 text-center text-[12px] transition-all ${
          dragOver ? "border-white bg-white/5 text-white" : "border-zinc-800 hover:border-zinc-600 text-zinc-500 hover:text-zinc-300"
        }`} data-testid={`upload-${block.id}`}>
          <input type="file" multiple onChange={onInput} className="hidden" disabled={busy} />
          {busy ? "Uploading…" : dragOver ? "Drop to upload" : "Click or drag to upload (multi-file)"}
        </label>
      )}
    </div>
  );
}
